import React, { useRef, useEffect, useState } from "react";
import { view } from "@risingstack/react-easy-state";
import "./ChatWidget.css";

const formatTimestamp = (timestamp) => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.error("Error formatting timestamp:", error);
    return timestamp;
  }
};

const ChatWidget = view(({ messages, onSendMessage, recipient }) => {
  const [messageInput, setMessageInput] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const chatViewerRef = useRef(null);
  const prevMessagesRef = useRef([]);
  const toggleButtonRef = useRef(null);

  useEffect(() => {
    if (chatViewerRef.current) {
      chatViewerRef.current.scrollTop = chatViewerRef.current.scrollHeight;
    }
  }, [messages]);

  // Track new messages and update unread count
  useEffect(() => {
    if (!isExpanded) {
      const newMessages = messages.filter((msg) => {
        return (
          !msg.isSentMessage &&
          !prevMessagesRef.current.some(
            (prevMsg) => prevMsg.timestamp === msg.timestamp
          )
        );
      });
      if (newMessages.length > 0) {
        setUnreadCount((prev) => prev + newMessages.length);
      }
    }
    prevMessagesRef.current = messages;
  }, [messages, isExpanded]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (messageInput.trim()) {
      onSendMessage(messageInput);
      setMessageInput("");
    }
  };

  const toggleWidget = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setUnreadCount(0);
    }
  };

  return (
    <div className={`chat-widget-container ${isExpanded ? "expanded" : ""}`}>
      {!isExpanded ? (
        <button
          ref={toggleButtonRef}
          className="chat-toggle-button"
          onClick={toggleWidget}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount}</span>
          )}
        </button>
      ) : (
        <div className="chat-widget">
          <div className="chat-header">
            <div className="chat-header-content">
              <h3>Chat with {recipient}</h3>
              {recipient !== "Everyone" && (
                <button
                  className="return-broadcast-btn"
                  onClick={() => onSendMessage("__switch_to_broadcast__")}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 12H5M5 12L12 19M5 12L12 5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Return to Broadcast
                </button>
              )}
            </div>
            <button
              className="minimize-button"
              onClick={toggleWidget}
              title="Close chat"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="chat-messages" ref={chatViewerRef}>
            {messages.map((message, index) => (
              <div
                key={index}
                className={`message ${
                  message.sender === "You" ? "sent-message" : "received-message"
                }`}
              >
                <div className="message-bubble">
                  {message.sender !== "You" && (
                    <div className="message-sender">{message.sender}</div>
                  )}
                  <div className="message-content">
                    {message.message}
                    {message.type === "broadcast" && (
                      <span className="message-type-badge">Broadcast</span>
                    )}
                  </div>
                  <span className="message-time">
                    {formatTimestamp(message.timestamp)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <form className="chat-input" onSubmit={handleSubmit}>
            <div className="input-wrapper">
              <input
                type="text"
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                placeholder={`Message ${recipient}...`}
                className="message-input"
              />
              <button
                type="submit"
                className="send-button"
                title="Send message"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22 2L11 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 2L15 22L11 13L2 9L22 2Z"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
});

export default ChatWidget;
